import { useEffect, useRef, useState } from "react";

export interface PlaceResult {
  location: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
}

interface Suggestion {
  placeId: string;
  description: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSelect: (result: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}

export function PlacesAutocompleteInput({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address...",
  className,
  inputClassName,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autocompleteAbortRef = useRef<AbortController | null>(null);
  const detailsAbortRef = useRef<AbortController | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cancel any in-flight requests on unmount so late responses don't try to
  // setState after the input is gone.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      autocompleteAbortRef.current?.abort();
      detailsAbortRef.current?.abort();
    };
  }, []);

  function handleChange(text: string) {
    onChange(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Abort any prior in-flight autocomplete request — a stale response from
    // an older keystroke must not overwrite suggestions for the current one.
    autocompleteAbortRef.current?.abort();
    if (!text.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      autocompleteAbortRef.current = controller;
      try {
        const res = await fetch(`/api/places/autocomplete?input=${encodeURIComponent(text)}`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const data = (await res.json()) as Suggestion[];
        setSuggestions(data);
        setOpen(data.length > 0);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        setSuggestions([]);
      }
    }, 300);
  }

  async function handleSelect(suggestion: Suggestion) {
    onChange(suggestion.description);
    setSuggestions([]);
    setOpen(false);
    detailsAbortRef.current?.abort();
    const controller = new AbortController();
    detailsAbortRef.current = controller;
    try {
      const res = await fetch(
        `/api/places/details?place_id=${encodeURIComponent(suggestion.placeId)}`,
        { signal: controller.signal },
      );
      if (!res.ok) return;
      const details = (await res.json()) as {
        formattedAddress: string;
        addressLine1: string;
        city: string;
        state: string;
        zip: string;
      };
      onSelect({
        location: details.formattedAddress || suggestion.description,
        addressLine1: details.addressLine1,
        city: details.city,
        state: details.state,
        zip: details.zip,
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      onSelect({
        location: suggestion.description,
        addressLine1: "",
        city: "",
        state: "",
        zip: "",
      });
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {suggestions.map((s) => (
            <li key={s.placeId}>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 transition-colors"
                onMouseDown={(e) => {
                  e.preventDefault();
                  void handleSelect(s);
                }}
              >
                {s.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
